/**
 * The hub card: the next one or two dates coming up, at a glance.
 *
 * Prop-free on purpose — the integrator drops <BirthdaysCard /> on the home screen
 * (adult AND kid views; everyone sees birthdays) and it feeds itself. Day one it is
 * empty, and empty is the state most people meet first, so it gets a real invitation
 * rather than a shrug. A failed read is never shown as "nothing coming up".
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { SpecialDate } from '../../shared/types';
import {
  KindIcon,
  KIND_META,
  cx,
  daysAwayLabel,
  explainError,
  formatDate,
  turningLabel,
} from './shared';

const SHOW = 2;

export default function BirthdaysCard() {
  const [dates, setDates] = useState<SpecialDate[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<SpecialDate[]>('/birthdays')
      .then((rows) => {
        if (cancelled) return;
        setDates(rows);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(explainError(err, 'Could not load birthdays.'));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const upcoming = dates?.slice(0, SHOW) ?? [];

  return (
    <section className="rounded-card border border-border bg-surface p-5 shadow-[0_1px_3px_rgba(43,39,35,0.05)] sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-ink-soft uppercase">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 text-ink-faint"
            aria-hidden="true"
          >
            <path d="M4 20h16" />
            <path d="M4 20v-6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6" />
            <path d="M4 15c1.2 1 2 1 3.2 0s2-1 3.2 0 2 1 3.2 0 2-1 3.2 0" />
            <path d="M12 8.5v3M8.5 9v2.5M15.5 9v2.5" />
          </svg>
          Birthdays
        </h2>
        <Link
          to="/birthdays"
          className="-mr-2 inline-flex min-h-11 items-center rounded-full px-2 text-sm font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          See all
        </Link>
      </div>

      <div className="mt-4">
        {loading && (
          <div className="space-y-3" role="status" aria-live="polite">
            <span className="sr-only">Loading birthdays…</span>
            {[0, 1].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-11 w-11 shrink-0 animate-pulse rounded-full bg-surface-sunk" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-28 animate-pulse rounded-full bg-surface-sunk" />
                  <div className="h-3 w-36 animate-pulse rounded-full bg-surface-sunk" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* A failed read is never rendered as "nothing coming up". */}
        {!loading && error && (
          <div className="space-y-3">
            <p className="flex items-start gap-2 text-sm text-clay">
              <span aria-hidden="true" className="font-semibold">
                !
              </span>
              <span>{error}</span>
            </p>
            <button
              type="button"
              onClick={() => setNonce((n) => n + 1)}
              className="inline-flex min-h-11 items-center rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && upcoming.length === 0 && (
          <div className="text-center">
            <p className="font-display text-lg font-semibold text-ink">No dates yet.</p>
            <p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-ink-soft">
              Add a birthday or anniversary and the house will remember it for you.
            </p>
            <Link
              to="/birthdays"
              className="mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-full bg-sage px-4 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              Add a date
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        )}

        {!loading && !error && upcoming.length > 0 && (
          <ul className="space-y-4">
            {upcoming.map((d) => {
              const turning = turningLabel(d);
              const soon = d.days_away <= 3;
              return (
                <li key={d.id} className="flex items-center gap-3">
                  <KindIcon kind={d.kind} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">
                      {d.name}
                      {turning && (
                        <span className="font-normal text-ink-faint"> · {turning}</span>
                      )}
                    </p>
                    <p className="text-sm text-ink-soft">
                      <span className={cx('font-medium', soon ? 'text-clay' : 'text-ink')}>
                        {daysAwayLabel(d.days_away)}
                      </span>
                      {' · '}
                      {formatDate(d.next_at)}
                    </p>
                  </div>
                  <span className="sr-only">{KIND_META[d.kind].label}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
